import {Day} from "@progress/kendo-date-math"
import {classNames} from "@progress/kendo-react-common"
import {useInternationalization} from "@progress/kendo-react-intl"
import {
    Scheduler,
    SchedulerDataChangeEvent,
    SchedulerItem,
    SchedulerItemMouseEvent,
    SchedulerItemProps,
    SchedulerSlot,
    SchedulerSlotMouseEvent,
    SchedulerSlotProps,
    SchedulerViewSlot,
    SchedulerViewSlotProps,
    WeekView,
    WorkWeekView,
} from "@progress/kendo-react-scheduler"
import {Tooltip} from "@progress/kendo-react-tooltip"
import dayjs from "dayjs"
import weekOfYear from "dayjs/plugin/weekOfYear"
import {useEffect, useState} from "react"
import {
    NewBookingParams,
    setNewBookingDetail,
    setPlannerTestPackge,
    setSelectedBookingId,
    setWeeklyDate,
    toggleBreakDialog,
    toggleTestDialog,
    WeeklyBooking,
} from "../../containers/MotBooking/bookingSlice"
import {RootState} from "../../rootReducer"
import {LoadingPanel} from "../../shared/CommonItems"
import {useAppDispatch, useAppSelector} from "../../store"
import {calltoPreparerDealerName} from "../../utils/common"
import {PlannerSchedulerCustomHeader} from "./controls/plannerSchedulerCustomHeader"
dayjs.extend(weekOfYear)

const timeout = (delay: number) => {
    return new Promise(res => setTimeout(res, delay))
}

const PlannerScheduler = () => {
    const dispatch = useAppDispatch()
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const selectedPackage = useAppSelector((state: RootState) => state.containers.motBooking.selectedPackagePlanner)
    const selectedWeeklyDate = useAppSelector((state: RootState) => state.containers.motBooking.selectedWeeklyDate)
    const plannerInterval = useAppSelector((state: RootState) => state.containers.motBooking.plannerInterval)

    const testPackages = useAppSelector((state: RootState) => state.containers.motBooking.plannerTestPackages)
    const loading = useAppSelector((state: RootState) => state.containers.motBooking.gettingWeeklyBookings)
    const bookings = useAppSelector((state: RootState) => state.containers.motBooking.weeklyBookings)
    //const userDealers = useAppSelector((state: RootState) => state.application.userDealers)

    const dealerSchedules = useAppSelector((state: RootState) => state.application.schedulesForDealer)

    const intl = useInternationalization()

    const [translatedSchedules, setTranslatedSchedules] = useState<WeeklyBooking[]>([] as WeeklyBooking[])

    const modelFields: any = {
        start: "testStart",
        end: "testEnd",
    }

    useEffect(() => {
        if (bookings && bookings.length > 0) {
            setTranslatedSchedules([])
            const newSchs = [] as WeeklyBooking[]
            bookings.forEach((booking: WeeklyBooking) => {
                const {
                    testStart,
                    testEnd,
                    bookingTypeID,
                    testPackage,
                    registration,
                    bookingStatus,
                    customer,
                    accountNo,
                    internal,
                } = booking
                const duration = dayjs(testEnd).diff(testStart, "m")
                newSchs.push({
                    ...booking,
                    id: parseInt(crypto.randomUUID()),
                    testStart: new Date(testStart),
                    testEnd: new Date(testEnd),
                    registration: registration ?? "",
                    testPackage: testPackage ?? "",
                    duration: `${duration} mins`,
                    bookingStatus: bookingStatus ?? "",
                    customer: customer,
                    accountNo: accountNo,
                    internal: internal,
                    title:
                        bookingTypeID === 1
                            ? `${intl.formatDate(new Date(testStart), {
                                  time: "short",
                              })}-${intl.formatDate(new Date(testEnd), {time: "short"})}  ${
                                  accountNo.length > 0 ? accountNo + " - " + customer : customer
                              }  ${registration ?? ""}`
                            : bookingTypeID === 2
                            ? `${intl.formatDate(new Date(testStart), {time: "short"})}-${intl.formatDate(
                                  new Date(testEnd),
                                  {time: "short"},
                              )} Break`
                            : `${intl.formatDate(new Date(testStart), {time: "short"})}-${intl.formatDate(
                                  new Date(testEnd),
                                  {time: "short"},
                              )} Available`,
                })
            })
            setTimeout(() => setTranslatedSchedules(newSchs), 1)
        } else {
            setTranslatedSchedules([])
        }
    }, [bookings])

    const toggleBookingDialog = (bookingTypeID: number, state: boolean) => {
        if (bookingTypeID === 1) dispatch(toggleTestDialog(state))
        if (bookingTypeID === 2) dispatch(toggleBreakDialog(state))
    }

    const setBookingItemColor = (dataItem: WeeklyBooking) => {
        if (dataItem.bookingTypeID === 1 && dataItem.internal && dataItem.bookingStatusId !== 3) {
            return "booked-color"
        } else if (dataItem.bookingTypeID === 2 || (dataItem.bookingTypeID === 1 && dataItem.bookingStatusId === 3)) {
            return "break-color"
        } else if (dataItem.bookingID === 0) {
            return "available-color"
        }
        return "external-color"
    }

    const setBookingItemTemplate = (title: string, dataItem: WeeklyBooking, isLoading: boolean) => {
        return (
            <div style={{height: "inherit"}} className="d-flex flex-column justify-content-start" title={title}>
                <div title={title}>
                    {intl.formatDate(new Date(dataItem["testStart"]), {time: "short"}) +
                        " - " +
                        intl.formatDate(new Date(dataItem["testEnd"]), {time: "short"})}
                </div>
                {dataItem.bookingID > 0 && dataItem.bookingTypeID === 2 && <div title={title}>Break</div>}
                {dataItem.bookingID > 0 && dataItem.bookingTypeID === 1 && (
                    <>
                        <div title={title} className="ms-2">{`${dataItem["customer"]}`}</div>
                        <div title={title}>{dataItem["registration"]}</div>
                    </>
                )}
                <div>{isLoading && <LoadingPanel />}</div>
            </div>
        )
    }

    const CustomSchedulerItem = (props: SchedulerItemProps) => {
        const [loading, setLoader] = useState(false)

        return (
            <Tooltip openDelay={100} position="bottom" anchorElement="pointer">
                <SchedulerItem
                    {...props}
                    onDoubleClick={async (e: SchedulerItemMouseEvent) => {
                        setLoader(true)
                        if (props.dataItem.bookingID === 0) {
                            const data = props.dataItem as WeeklyBooking
                            // const selectedPreparerDealerName = calltoPreparerDealerName(
                            //     userDealers,
                            //     selectedDealer?.dealerId,
                            // )

                            let params = {
                                dealerId: selectedDealer?.dealerId,
                                testStart: data.testStart.toString(),
                                testEnd: data.testEnd.toString(),
                                testPackageID: data.testPackageId,
                                registration: null,
                                customer: null,
                                testFee: data.testFee,
                                laneFee: data.laneFee,
                                preparerDealerName: "",
                                preparedByDealer: false,
                                // preparerDealerName:
                                //     selectedPreparerDealerName !== undefined ? selectedPreparerDealerName : null,
                                // preparedByDealer: selectedPreparerDealerName !== undefined ? true : false,
                            } as NewBookingParams
                            dispatch(setNewBookingDetail(params))
                        } else {
                            dispatch(setSelectedBookingId(props.dataItem.bookingID))
                        }
                        await timeout(200)
                        toggleBookingDialog(props.dataItem.bookingID > 0 ? props.dataItem.bookingTypeID : 1, true)
                        setLoader(false)
                    }}
                    children={setBookingItemTemplate(props.title ?? "", props.dataItem, loading)}
                    style={{
                        ...props.style,
                        textAlign: "center",
                    }}
                    //dragHint={!isEditable}
                    className={`${setBookingItemColor(props.dataItem)}`}
                />
            </Tooltip>
        )
    }

    const CustomSchedulerSlot = (props: SchedulerSlotProps) => {
        let validScheduleSlot = false
        let showStartBorder = false
        let showEndBorder = false

        const scheduleValueIndex = dealerSchedules
            .map(x => dayjs(x.startDateTime).format("DD/MM/YYYY"))
            .indexOf(dayjs(props.start).format("DD/MM/YYYY"))
        const hasScheduleForDay = scheduleValueIndex >= 0

        if (hasScheduleForDay) {
            validScheduleSlot =
                dayjs(props.start) >= dayjs(dealerSchedules[scheduleValueIndex].startDateTime) &&
                dayjs(dealerSchedules[scheduleValueIndex].endDateTime) >= dayjs(props.end)

            showStartBorder = dayjs(dealerSchedules[scheduleValueIndex].startDateTime) === dayjs(props.start)
            showEndBorder = dayjs(dealerSchedules[scheduleValueIndex].endDateTime) === dayjs(props.end)
        }

        const recExist = bookings.filter(
            x =>
                ((dayjs(x.testStart) <= dayjs(props.start) && dayjs(x.testEnd) >= dayjs(props.end)) ||
                    (dayjs(props.start) < dayjs(x.testEnd) && dayjs(props.end) > dayjs(x.testEnd)) ||
                    (dayjs(props.start) < dayjs(x.testStart) && dayjs(props.start) > dayjs(x.testStart))) &&
                (x.bookingStatusId === 1 || x.bookingStatusId === 2 || x.bookingStatusId === 4),
        )
        // const isEditable = dayjs(props.start).toDate() >= dayjs().toDate() && recExist.length === 0 && validScheduleSlot
        const isEditable = recExist.length === 0 && validScheduleSlot

        const className = classNames({
            "k-disabled": !isEditable,
            // "k-no-schedules": !validScheduleSlot,
            "k-scheduled-slot": validScheduleSlot,
        })
        return (
            <SchedulerSlot
                {...props}
                style={{
                    ...props.style,
                    borderBottom: showStartBorder ? "1px solid red" : undefined,
                    borderTop: showEndBorder ? "1px solid red" : undefined,
                }}
                className={className}
                onClick={(event: SchedulerSlotMouseEvent) => {
                    if (event.target.element) event.target.element.focus()
                }}
                onDoubleClick={(event: SchedulerSlotMouseEvent) => {
                    if (isEditable && validScheduleSlot) {
                        const testPackage = testPackages.find(x => x.testPackageID === selectedPackage)
                        dispatch(setSelectedBookingId(0))
                        // const selectedPreparerDealerName = calltoPreparerDealerName(
                        //     userDealers,
                        //     selectedDealer?.dealerId,
                        // )

                        let params = {
                            dealerId: selectedDealer?.dealerId,
                            testStart: props.start.toString(),
                            testEnd:
                                (testPackage && dayjs(props.start).add(testPackage?.duration, "minutes").toString()) ??
                                props.start.toString(),
                            testPackageID:
                                testPackage?.noofRecord !== undefined && testPackage.noofRepeat !== undefined
                                    ? testPackage.noofRecord > 1 && testPackage.noofRepeat >= 1
                                        ? 0
                                        : selectedPackage
                                    : 0,
                            registration: null,
                            customer: null,
                            testFee:
                                testPackage?.noofRecord !== undefined && testPackage.noofRepeat !== undefined
                                    ? testPackage.noofRecord > 1 && testPackage.noofRepeat >= 1
                                        ? 0
                                        : testPackage?.testFee
                                    : 0,
                            laneFee:
                                testPackage?.noofRecord !== undefined && testPackage.noofRepeat !== undefined
                                    ? testPackage.noofRecord > 1 && testPackage.noofRepeat >= 1
                                        ? 0
                                        : testPackage?.laneFee
                                    : 0,
                            preparerDealerName: "",
                            preparedByDealer: false,
                            // preparerDealerName:
                            //     selectedPreparerDealerName !== undefined ? selectedPreparerDealerName : null,
                            // preparedByDealer: selectedPreparerDealerName !== undefined ? true : false,
                        } as NewBookingParams
                        dispatch(setNewBookingDetail(params))
                        toggleBookingDialog(1, true)
                    }
                }}
            />
        )
    }

    const CustomViewSlot = (props: SchedulerViewSlotProps) => {
        const isEditable = new Date(props.start).getTime() >= new Date().getTime()
        return (
            <SchedulerViewSlot
                {...props}
                style={{...props.style, padding: 0, cursor: !isEditable ? "not-allowed" : undefined}}
            />
        )
    }

    function onDateChange(event: any) {
        let date = event.value
        dispatch(setWeeklyDate(date))
        dispatch(setPlannerTestPackge(0))
    }
    const handleDataChange = ({created, updated, deleted}: SchedulerDataChangeEvent) => {}

    const getMinStartDateForWeek = () => {
        const selectedWeek = dayjs(selectedWeeklyDate).week() - 1
        if (dealerSchedules.length > 0) {
            const weekSchedules = dealerSchedules.filter(x => dayjs(x.startDateTime).week() - 1 === selectedWeek)
            const startMin = weekSchedules.map(e => `${dayjs(e.startDateTime).format("HH:mm")}`).sort((a, b) => a.localeCompare(b))[0]
            return startMin
        }
        return "06:00"
    }

    const getMaxEndDateForWeek = () => {
        const selectedWeek = dayjs(selectedWeeklyDate).week() - 1
        if (dealerSchedules.length > 0) {
            const weekSchedules = dealerSchedules.filter(x => dayjs(x.startDateTime).week() - 1 === selectedWeek)
            const endMax = weekSchedules
                .map(e => `${dayjs(e.endDateTime).format("HH:mm")}`)
                .sort((a, b) => a.localeCompare(b))
                .reverse()[0]
            return endMax
        }
        return "22:00"
    }

    return (
        <div className="position-relative">
            {selectedDealer && loading && <LoadingPanel />}
            <Scheduler
                data={translatedSchedules}
                onDateChange={onDateChange}
                height="calc(100vh - 209px)"
                id="plannerScheduler"
                editable={{add: true, edit: false, remove: false, drag: false, resize: false, select: true}}
                modelFields={modelFields}
                item={CustomSchedulerItem}
                slot={CustomSchedulerSlot}
                viewSlot={CustomViewSlot}
                defaultView="week"
                onDataChange={handleDataChange}
                date={selectedWeeklyDate ?? undefined}
                header={PlannerSchedulerCustomHeader}
            >
                <WorkWeekView
                    slotDuration={plannerInterval}
                    slotDivisions={plannerInterval === 15 || plannerInterval === 45 ? 3 : 2}
                    showWorkHours={true}
                    title="Work Week"
                    workWeekStart={Day.Monday}
                    workWeekEnd={Day.Friday}
                    workDayStart={getMinStartDateForWeek()}
                    workDayEnd={getMaxEndDateForWeek()}
                />
                <WeekView
                    slotDuration={plannerInterval}
                    slotDivisions={plannerInterval === 15 || plannerInterval === 45 ? 3 : 2}
                    showWorkHours={true}
                    title="Full Week"
                    workWeekStart={Day.Monday}
                    workWeekEnd={Day.Saturday}
                    workDayStart={getMinStartDateForWeek()}
                    workDayEnd={getMaxEndDateForWeek()}
                />
            </Scheduler>
        </div>
    )
}

export default PlannerScheduler
